import { clamp } from 'lodash-es';

/**
 * Check color string and return what type it is. ('HEX', 'RGB', 'RGBA' or 'NONE')
 * @param colorStr
 * @returns {string} color type
 */
const getColorStringType = (colorStr: string): string => {
  const noneWhiteSpaceColorStr = colorStr.replace(/ /g, '');
  const isHEX = /^#(?:[A-Fa-f0-9]{3}){1,2}$/.exec(noneWhiteSpaceColorStr);
  const isRGB =
    /^rgb[(](?:\s*0*(?:\d\d?(?:\.\d+)?(?:\s*%)?|\.\d+\s*%|100(?:\.0*)?\s*%|(?:1\d\d|2[0-4]\d|25[0-5])(?:\.\d+)?)\s*(?:,(?![)])|(?=[)]))){3}[)]$/.exec(
      noneWhiteSpaceColorStr,
    );
  const isRGBA =
    /^rgba[(](?:\s*0*(?:\d\d?(?:\.\d+)?(?:\s*%)?|\.\d+\s*%|100(?:\.0*)?\s*%|(?:1\d\d|2[0-4]\d|25[0-5])(?:\.\d+)?)\s*,){3}\s*0*(?:\.\d+|1?)\s*[)]$/.exec(
      noneWhiteSpaceColorStr,
    );

  switch (true) {
    case !!isHEX:
      return 'HEX';
    case !!isRGB:
      return 'RGB';
    case !!isRGBA:
      return 'RGBA';
    default:
      return 'NONE';
  }
};

/**
 * get opacity value on rgba color string
 * ex) input  : rgba(255, 255, 255, 0.1)
 *     return : 0.1
 * @param rgbaColorString
 * @returns {string} opacity
 */
const getOpacity = (rgbaColorString: string): string => {
  const noneWhiteSpaceColorStr = rgbaColorString.replace(/ /g, '');
  const colorType = getColorStringType(noneWhiteSpaceColorStr);

  if (colorType === 'RGBA') {
    return noneWhiteSpaceColorStr.replace(/^.*,(.+)\)/, '$1');
  }

  return '1';
};

const hexToRGBNumber = (hex: string): number[] | null => {
  if (!hex) {
    return null;
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    return null;
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return [r, g, b];
};

/**
 * HEX 색상 문자열을 'r,g,b' 형식 문자열로 변환한다.
 * 파싱 실패 시 안전한 기본값('0,0,0')을 반환해 호출부의 `rgba(${...}, alpha)` 템플릿 보간이
 * 항상 유효한 CSS를 만들도록 보장한다.
 * @param hex HEX 색상 문자열 (예: '#FF0000')
 * @returns {string} 'r,g,b' 형식 문자열
 */
export const hexToRgb = (hex: string): string => {
  const result = hexToRGBNumber(hex);

  if (!result) {
    return '0,0,0';
  }

  const [r, g, b] = result;
  return `${r},${g},${b}`;
};

/**
 * Transforming color string to rgba code
 * Return BLACK ('rgba(0, 0, 0, ${opacity})') if fail transforming
 * @param colorStr        hex color code, rgb, rgba .. etc
 * @param opacity            color opacity. (default 1)translate
 * @returns {string} transformed rgba
 */
const colorStringToRgba = (colorStr: string, opacity = 1): string => {
  const noneWhiteSpaceColorStr = colorStr.replace(/ /g, '');
  const colorType = getColorStringType(noneWhiteSpaceColorStr);

  switch (colorType) {
    case 'HEX':
      return `rgba(${hexToRgb(noneWhiteSpaceColorStr)},${opacity})`;
    case 'RGB':
      return noneWhiteSpaceColorStr.replace(')', `, ${opacity})`).replace('rgb', 'rgba');
    case 'RGBA':
      return noneWhiteSpaceColorStr.replace(`${getOpacity(colorStr)})`, `${opacity})`);
    default:
      return `rgba(0, 0, 0, ${opacity})`;
  }
};

/**
 * Transforming Color number into hex value (XX)
 * Pads 0 when the value is under 16 (F)
 * @param number        number value of a color code .. 0 ~ 255
 * @returns {string} transformed hex code
 */
const toHex = (c: number): string => {
  const hex = Math.floor(c).toString(16);
  return hex.padStart(2, '0');
};

const getRGBANumber = (colorStr: string, type?: string): number[] => {
  const colorType = type || getColorStringType(colorStr);

  switch (colorType) {
    case 'RGB': {
      const rgbMatch = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(colorStr);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        return [r, g, b];
      }
      break;
    }
    case 'RGBA': {
      const rgbaMatch = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/.exec(
        colorStr,
      );
      if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1], 10);
        const g = parseInt(rgbaMatch[2], 10);
        const b = parseInt(rgbaMatch[3], 10);
        const alpha = parseFloat(rgbaMatch[4]);
        return [r, g, b, alpha];
      }
      break;
    }
    case 'HEX': {
      const rgbNumber = hexToRGBNumber(colorStr);
      if (rgbNumber) {
        return rgbNumber;
      }
      break;
    }
    default:
      return [0, 0, 0];
  }
  return [0, 0, 0];
};

const colorStringToHex = (colorStr: string, applyOpacity: boolean = false): string => {
  const colorType = getColorStringType(colorStr);
  if (colorType === 'HEX') {
    return colorStr;
  }

  const rgbNumber = getRGBANumber(colorStr, colorType);
  if (!rgbNumber) {
    return colorStr;
  }

  const [r, g, b, alpha] = rgbNumber;
  if (applyOpacity && alpha !== undefined) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha < 1 ? toHex(alpha * 255) : ''}`.toUpperCase();
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

/**
 * Merge two RGBA color if the opacity is set
 * uses alpha value to define the ratio of each color
 * @param baseColor        base color
 * @param altColor        another color which is to be merged into base color
 * @returns {string} Hex code of a merged color
 */
const mergeColorToHex = (baseColor?: string, altColor?: string): string | null => {
  if (!baseColor && !altColor) {
    return null;
  }
  if (!baseColor || !altColor) {
    return mergeColorToHex('#FFFFFF', baseColor || altColor);
  }

  const [baseR, baseG, baseB, baseA = 1] = getRGBANumber(baseColor);
  const [altR, altG, altB, altA = 1] = getRGBANumber(altColor);
  const mix = [0, 0, 0, 0];

  mix[3] = 1 - (1 - altA) * (1 - baseA); // alpha
  mix[0] = Math.round((altR * altA) / mix[3] + (baseR * baseA * (1 - altA)) / mix[3]);
  mix[1] = Math.round((altG * altA) / mix[3] + (baseG * baseA * (1 - altA)) / mix[3]);
  mix[2] = Math.round((altB * altA) / mix[3] + (baseB * baseA * (1 - altA)) / mix[3]);

  if (mix[3] < 1) {
    const WHITE = 255;
    const whiteA = 1 - mix[3];
    mix[0] = Math.round(WHITE * whiteA + mix[0] * mix[3] * (1 - whiteA));
    mix[1] = Math.round(WHITE * whiteA + mix[1] * mix[3] * (1 - whiteA));
    mix[2] = Math.round(WHITE * whiteA + mix[2] * mix[3] * (1 - whiteA));
  }

  return `#${toHex(mix[0])}${toHex(mix[1])}${toHex(mix[2])}`.toUpperCase();
};

/**
 * HSL 색상 공간을 RGB로 역변환할 때 하나의 채널 값을 계산합니다.
 * p와 q는 명도(L)와 채도(S)에서 파생된 최소·최대 밝기 한계이며,
 * t는 대상 색조(H)에 채널별로 적용한 보정 값입니다.
 *
 * @param p - 계산된 채널이 가질 수 있는 최소 밝기
 * @param q - 계산된 채널이 가질 수 있는 최대 밝기
 * @param t - 채널에 대응하는 색조 위치 (0~1 범위, 필요 시 순환 처리)
 * @returns 0~1 범위의 정규화된 RGB 채널 값
 */
const hueToRgb = (p: number, q: number, t: number): number => {
  let temp = t;
  if (temp < 0) {
    temp += 1;
  }
  if (temp > 1) {
    temp -= 1;
  }
  if (temp < 1 / 6) {
    return p + (q - p) * 6 * temp;
  }
  if (temp < 1 / 2) {
    return q;
  }
  if (temp < 2 / 3) {
    return p + (q - p) * (2 / 3 - temp) * 6;
  }
  return p;
};

/**
 * 입력된 색상을 HSL 색공간으로 변환한 뒤 Lightness(L) 값을 선형으로 이동시켜 밝기를 조절합니다.
 * HEX, RGB, RGBA 문자열을 모두 지원하며 채도(S)를 유지한 상태에서 명도만 변화시킵니다.
 * percent는 -100 ~ 100 범위의 백분율 오프셋으로, Lightness에 percent/100을 더한 값이 사용됩니다.
 * 양수는 밝게, 음수는 어둡게 조절되며, 결과는 0~1 범위로 클램핑됩니다.
 *
 * @param colorStr - 조절할 색상 문자열 (HEX, RGB, RGBA 형식 지원)
 * @param percent - Lightness에 더할 백분율 오프셋
 * @returns {string} 조정된 색상을 나타내는 RGBA 문자열
 */
const adjustColorBrightness = (colorStr: string, percent: number): string => {
  if (!colorStr) return '';

  const colorType = getColorStringType(colorStr);

  const [r, g, b, a = 1] = getRGBANumber(colorStr, colorType);

  const rNorm = clamp(r / 255, 0, 1);
  const gNorm = clamp(g / 255, 0, 1);
  const bNorm = clamp(b / 255, 0, 1);

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let hue = 0;
  let saturation = 0;
  let lightness = (max + min) / 2;

  if (delta > 0) {
    const denominator = 1 - Math.abs(2 * lightness - 1);
    saturation = denominator === 0 ? 0 : delta / denominator;

    if (Math.abs(max - rNorm) < Number.EPSILON) {
      hue = (gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0);
    } else if (Math.abs(max - gNorm) < Number.EPSILON) {
      hue = (bNorm - rNorm) / delta + 2;
    } else {
      hue = (rNorm - gNorm) / delta + 4;
    }

    hue /= 6;
  }

  lightness = clamp(lightness + percent / 100, 0, 1);

  let adjustedR = lightness;
  let adjustedG = lightness;
  let adjustedB = lightness;

  if (saturation > 0) {
    const q =
      lightness < 0.5
        ? lightness * (1 + saturation)
        : lightness + saturation - lightness * saturation;
    const p = 2 * lightness - q;

    adjustedR = hueToRgb(p, q, hue + 1 / 3);
    adjustedG = hueToRgb(p, q, hue);
    adjustedB = hueToRgb(p, q, hue - 1 / 3);
  }

  const finalR = Math.round(clamp(adjustedR * 255, 0, 255));
  const finalG = Math.round(clamp(adjustedG * 255, 0, 255));
  const finalB = Math.round(clamp(adjustedB * 255, 0, 255));

  return `rgba(${finalR}, ${finalG}, ${finalB}, ${a})`;
};

export {
  getColorStringType,
  getOpacity,
  colorStringToRgba,
  colorStringToHex,
  mergeColorToHex,
  adjustColorBrightness,
};
